import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistoryDismiss } from './use-history-dismiss.js';
import { api, call } from '../platform/api.js';
import { navigate } from '../platform/router.js';
import { NEW_BOX_ITEMS_KEY } from '../platform/box-handoff.js';
import { OptionSheet } from './option-sheet.js';
import { useConfirm } from './confirm.js';
import { AddFieldButton } from './structure-menu.js';
import {
  brandManage,
  builtinLabel,
  metaCan,
  categoryManage,
  fieldOptionManage,
  locationManage,
  type StructureUsage,
} from './structure-manage.js';
import type { IntakeMeta } from '../../../api/src/catalog/intake.js';
import { readIntakeMeta } from '../platform/intake-meta.js';
import type { SourceRow } from '../../../api/src/catalog/sources.js';
import type { BookingListRow } from '../../../api/src/catalog/bookings.js';
import { rangeLabel } from '../screens/booking-range.js';

/**
 * Bulk edit sheet — one deliberate change, named in full before it happens.
 *
 * The server refuses a payload with more than one change because the confirm sentence
 * must name its exact effect (standing rule for destructive scope); this sheet is the
 * client half of that contract: pick ONE target, pick ONE value (or Clear), read the
 * sentence, apply. Multi-attribute cleanups are successive confirmed passes.
 *
 * The result line renders the SERVER's verification count — `verified` comes from a
 * same-transaction re-read, and `missing` (items deleted meanwhile) is said out loud,
 * never absorbed (G10). Online-only by design: bulk writes are never queued for replay.
 */

type Target =
  | { kind: 'brand' }
  | { kind: 'category' }
  | { kind: 'location' }
  | { kind: 'field'; key: string }
  /** Retire/restore: a whole-selection state change, not a value to pick. */
  | { kind: 'retire'; retired: boolean }
  /** Assign to a source order (null clears - and the deadline goes with it). */
  | { kind: 'order'; orderId: string | null; label: string }
  /** Add to a reservation — not a bulk field write: it rides addBookingItems, whose
   * draft/confirmed distinction (warn vs refuse) is named in the sentence. */
  | { kind: 'bookingAdd'; bookingId: string; label: string; status: string };

/** null = Clear. */
type Pending = { target: Target; values: string[] | null };

export function BulkEdit({
  itemIds,
  openAt,
  onDone,
  onClose,
}: {
  itemIds: string[];
  /**
   * Skip the target list and open straight at this act. Retiring is a first-class
   * cleanup act (field report, 27 Jul: the maintainer went looking for it in the
   * toolbar and found only "Edit N selected…"), so the list screen offers it directly —
   * but it still lands on the SAME confirm sentence, never on the apply.
   *
   * `'booking'` is the same complaint, reported by the atelier on 30 Jul about the same
   * menu: they selected two garments, went looking for a check-out, and add-to-booking
   * was three taps deep inside EDIT. Opening the booking PICKER, not a confirm — which
   * booking is a choice, not a default, and there is no sensible one to preselect.
   */
  openAt?: 'retire' | 'restore' | 'booking' | 'outfit' | 'order' | undefined;
  /** Called after a verified apply with the user-facing summary line. */
  onDone: (summary: string) => void;
  onClose: () => void;
}): JSX.Element {
  // Back / edge-swipe closes the whole edit flow instead of popping the route (S2 part 3);
  // a picker opened inside it pushes its own entry, so back peels the picker off first.
  useHistoryDismiss(onClose);
  // The brand-MERGE confirm (rename-onto-existing) — `brandManage` is React-free, so the hook lives
  // here and is injected in (S2-c retired its `window.confirm`). `confirmEl` renders over the
  // picker below.
  const [confirmEl, confirmMerge] = useConfirm();
  const [meta, setMeta] = useState<IntakeMeta | null>(null);
  /** Blast-radius counts behind the ✕ in the category and location pickers. */
  const [structureUsage, setStructureUsage] = useState<StructureUsage | null>(null);
  /** What a brand merge actually moved — a bulk act says its own number. */
  const [bulkOutcome, setBulkOutcome] = useState<string | null>(null);
  const reloadUsage = useCallback(async () => {
    const structure = await call('structureUsage', () => api.structureUsage.query());
    if (structure.ok) setStructureUsage(structure.data);
  }, []);
  const [sources, setSources] = useState<SourceRow[] | null>(null);
  const [pickingOrder, setPickingOrder] = useState(openAt === 'order');
  const [bookings, setBookings] = useState<BookingListRow[] | null>(null);
  const [pickingBooking, setPickingBooking] = useState(openAt === 'booking');
  const [problem, setProblem] = useState<string | null>(null);
  const [picking, setPicking] = useState<Target | null>(null);
  const [pending, setPending] = useState<Pending | null>(
    openAt === 'retire'
      ? { target: { kind: 'retire', retired: true }, values: null }
      : // `restore` is the same target with the flag flipped — it left the sheet's own list
        // in 0.1.171 and is opened from the acts column, beside the Retire it undoes.
        openAt === 'restore'
        ? { target: { kind: 'retire', retired: false }, values: null }
        : null,
  );
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    void (async () => {
      const result = await readIntakeMeta();
      if (result.ok) setMeta(result.data);
      else setProblem(result.failure.message);
    })();
    void reloadUsage();
  }, [reloadUsage]);

  /**
   * Bookings load whenever the picker is showing and they are not in hand yet — which
   * covers BOTH doors: the in-sheet "Add to a reservation…" and `openAt='booking'`, where
   * there is no click to hang the fetch on. Keyed on the picker rather than duplicated
   * at each entry point, so a third door cannot forget it.
   *
   * A failure sets `problem` rather than leaving "Loading bookings…" forever. An empty
   * list and an unreachable server look identical on screen otherwise, which is the
   * silent-failure shape this project keeps paying for.
   */
  /** Sources load whenever the order picker is showing and they are not in hand — the
   * same both-doors reasoning as the booking picker below. */
  useEffect(() => {
    if (!pickingOrder || sources !== null) return;
    void (async () => {
      const result = await call('listSources', () => api.listSources.query());
      if (result.ok) setSources(result.data);
      else setProblem(result.failure.message);
    })();
  }, [pickingOrder, sources]);

  useEffect(() => {
    if (!pickingBooking || bookings !== null) return;
    void (async () => {
      const result = await call('listBookings', () => api.listBookings.query());
      if (result.ok) setBookings(result.data);
      else setProblem(result.failure.message);
    })();
  }, [pickingBooking, bookings]);

  const n = itemIds.length;

  /**
   * Cancelling a picker you were TAKEN to should leave, not strand you somewhere else.
   *
   * When the sheet is opened by a promoted chip (`openAt`), the picker IS the screen the
   * user asked for — falling back to the field list drops them into an Edit sheet they
   * never opened, and because that sheet is a dialog it then covers the selection bar and
   * swallows every click. That is what it did on its first run, fifteen times in one
   * suite. Opened from INSIDE the sheet, the list is where they came from, so back is
   * back.
   */
  const leavePicker = (setter: (v: boolean) => void): void => {
    if (openAt === undefined) setter(false);
    else onClose();
  };

  /**
   * `openAt='outfit'` runs the act immediately. Naming an outfit is a prompt, not a
   * picker, so there is no screen to open at — the sheet exists only to host the call
   * and report its sentence. Guarded by a ref so React's double-invoke in development
   * cannot ask for the name twice.
   */
  const outfitAsked = useRef(false);
  useEffect(() => {
    if (openAt !== 'outfit' || outfitAsked.current || meta === null) return;
    outfitAsked.current = true;
    void saveAsOutfit().then((went) => {
      // Backed out of the name prompt: this door exists only to ask, so close rather
      // than leave an empty dialog covering the list.
      if (!went) onClose();
    });
  }, [openAt, meta]);

  const targetLabel = (target: Target): string => {
    if (target.kind === 'field') return meta?.fields.find((f) => f.key === target.key)?.label ?? target.key;
    if (target.kind === 'retire') return target.retired ? 'Retire' : 'Restore';
    if (target.kind === 'order') return 'Source order';
    if (target.kind === 'bookingAdd') return 'Booking';
    // The three built-in headings honour the tenant's own name for them (025). This one
    // function feeds BOTH the row label and the picker's title, so a rename that reached
    // only one of them would put two words for one thing on the same screen.
    return builtinLabel(meta?.labels, target.kind);
  };

  /** The confirm sentence IS the contract — it names change, value and count. */
  const sentence = (p: Pending): string => {
    const label = targetLabel(p.target);
    const noun = `${String(n)} item${n === 1 ? '' : 's'}`;
    // Retirement is a verb, not a field being set — "Retire 12 items" is the sentence
    // a person would say, and the confirm copy must be one of those.
    if (p.target.kind === 'retire') {
      return p.target.retired
        ? `Retire ${noun} (hidden from the list; nothing deleted)`
        : `Restore ${noun} to available`;
    }
    if (p.target.kind === 'order') {
      // The deadline consequence is IN the sentence - inheriting or clearing a date
      // is the real effect, and a confirm that omits it names half the act.
      return p.target.orderId === null
        ? `Clear the source order on ${noun} (their return deadlines are cleared too)`
        : `Assign ${noun} to ${p.target.label} (their return deadlines will follow the order)`;
    }
    if (p.target.kind === 'bookingAdd') {
      // The engine's consequence is in the sentence. Only drafts are offered (0.1.523).
      return `Add ${noun} to ${p.target.label} (a draft — clashes come back as warnings, not refusals)`;
    }
    if (p.values === null || p.values.length === 0) return `Clear ${label} on ${noun}`;
    return `Set ${label} to ${p.values.map((v) => `“${v}”`).join(', ')} on ${noun}`;
  };

  /** NO PROMPT SINCE 0.1.267 — the LAST of §4.1's nine (rule 1): the outfit is created
   * immediately with a derived name ("Outfit N", server-side, same numbering rule as
   * boxes) and renamed in place on the Outfits screen. Not a Pending: there is no value
   * to pick and nothing destructive to confirm (an outfit holds nothing).
   *
   * Always returns true now — with no prompt there is no back-out path; the create is
   * the act. The boolean stays so the openAt='outfit' door keeps its close-again shape. */
  const saveAsOutfit = async (): Promise<boolean> => {
    if (applying) return true;
    setApplying(true);
    setProblem(null);
    const result = await call('createOutfit', () => api.createOutfit.mutate({ itemIds }));
    setApplying(false);
    if (!result.ok) {
      setProblem(result.failure.message);
      return true; // stay open: the sheet is where the refusal is readable
    }
    onDone(`Outfit “${result.data.name}” saved with ${String(n)} item${n === 1 ? '' : 's'} ✓ — name it on the Outfits screen`);
    return true;
  };

  const apply = async (p: Pending): Promise<void> => {
    if (applying || meta === null) return;
    setApplying(true);
    setProblem(null);

    // Bookings ride their own procedure, not the bulk field write: the engine's
    // warnings (draft clashes) have to reach the summary line, and bulk's verified
    // count has no place for them.
    if (p.target.kind === 'bookingAdd') {
      const bookingId = p.target.bookingId;
      const result = await call('addBookingItems', () => api.addBookingItems.mutate({ bookingId, itemIds }));
      setApplying(false);
      if (!result.ok) {
        setProblem(result.failure.message);
        return;
      }
      const { added, alreadyIn, warnings } = result.data;
      onDone(
        `${sentence(p)} — ${String(added)} added ✓` +
          (alreadyIn > 0 ? ` (${String(alreadyIn)} already in it)` : '') +
          (warnings.length > 0
            ? ` — ⚠ ${String(warnings.length)} clash${warnings.length === 1 ? '' : 'es'} with other bookings (shown on the booking)`
            : ''),
      );
      return;
    }

    let set: Record<string, unknown>;
    if (p.target.kind === 'brand') {
      set = { brand: p.values?.[0] ?? null };
    } else if (p.target.kind === 'category' || p.target.kind === 'location') {
      // Name → id, refusing a miss: coercing a failed lookup to null would turn
      // "Set Category to X" into a silent Clear — a change nobody confirmed.
      const name = p.values?.[0];
      let id: string | null = null;
      if (name !== undefined) {
        const found =
          p.target.kind === 'category'
            ? meta.categories.find((c) => c.path === name)?.id
            : meta.locations.find((l) => l.name === name)?.id;
        if (found === undefined) {
          setApplying(false);
          setProblem(`“${name}” no longer exists — reload and pick again.`);
          return;
        }
        id = found;
      }
      set = p.target.kind === 'category' ? { categoryId: id } : { locationId: id };
    } else if (p.target.kind === 'retire') {
      set = { retired: p.target.retired };
    } else if (p.target.kind === 'order') {
      set = { sourceOrderId: p.target.orderId };
    } else {
      set = { field: { key: p.target.key, values: p.values ?? [] } };
    }

    const result = await call('bulkUpdateItems', () => api.bulkUpdateItems.mutate({ itemIds, set }));
    setApplying(false);
    if (!result.ok) {
      setProblem(result.failure.message);
      return;
    }
    const { verified, missing } = result.data;
    const summary =
      `${sentence(p)} — ${String(verified)} verified ✓` +
      (missing.length > 0 ? ` (${String(missing.length)} no longer exist and were skipped)` : '');
    onDone(summary);
  };

  if (problem !== null && meta === null) {
    return (
      <div className="sheet" role="dialog" aria-label="Bulk edit">
        <p role="alert">{problem}</p>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }
  if (meta === null) return <p role="status">Loading…</p>;

  /**
   * ⚠️ `openAt='outfit'` HAS NO SCREEN, AND MUST NOT FALL THROUGH TO ONE.
   *
   * Reported 1 Aug 2026: "selecting items and then clicking save as outfit currently opens
   * both save as outfits pop up and the edit items pop up." This door exists only to HOST
   * the save (the prompt it hosted died in 0.1.267) — but none of the
   * branches below match `outfit`, so render fell all the way through to the "Edit N items"
   * sheet and painted it behind the prompt. Two dialogs for one act, and the one the user
   * did not ask for is the one still on screen afterwards.
   *
   * 0.1.89 fixed HALF of this — the cancel path, which used to leave an empty dialog
   * swallowing every click — by calling `onClose()` when the prompt was dismissed. The
   * success path and the in-flight window were never covered, because the fix was aimed at
   * the empty dialog rather than at the fall-through that produced it.
   *
   * A refusal still renders, because `saveAsOutfit` deliberately stays open on failure:
   * this is the only place the server's sentence can be read.
   */
  if (openAt === 'outfit') {
    if (problem !== null) {
      return (
        <div className="sheet" role="dialog" aria-label="Save as outfit">
          <p role="alert">{problem}</p>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      );
    }
    // Not `null`: the save is a real request and a screen that goes blank during it reads
    // as the app having lost the selection. `role="status"` rather than a dialog, so it
    // announces without covering anything.
    return <p role="status">{applying ? 'Saving the outfit…' : ''}</p>;
  }

  if (pickingOrder) {
    if (sources === null) return <p role="status">Loading orders…</p>;
    const orders = sources.flatMap((src) =>
      src.orders.map((o) => ({
        id: o.id,
        label: `“${src.name}${o.reference !== null ? ` — ${o.reference}` : ''}”${o.returnDueOn !== null ? ` (due ${o.returnDueOn})` : ''}`,
      })),
    );
    return (
      <div className="sheet" role="dialog" aria-label="Source order">
        <header className="sheet-header">
          <strong>Assign to which order?</strong>
          <button type="button" onClick={() => leavePicker(setPickingOrder)}>
            Cancel
          </button>
        </header>
        {/* A NEW box, first — before the list of existing ones, because "this selection IS
            the new delivery" is the commoner case at the door. Lives here rather than as
            its own toolbar chip: an extra chip wrapped the toolbar to a second line and
            shifted the rows mid-drag, which select.spec.ts exists to forbid. The
            selection rides sessionStorage — a hundred uuids is not a URL — and the
            create screen clears it on save. */}
        <p>
          <button
            type="button"
            className="sheet-option"
            onClick={() => {
              sessionStorage.setItem(NEW_BOX_ITEMS_KEY, JSON.stringify(itemIds));
              navigate({ name: 'boxAdd' });
            }}
          >
            ＋ New box from {String(itemIds.length)} selected…
          </button>
        </p>
        {orders.length === 0 && <p>No source orders exist yet — record one on the Deadlines screen first.</p>}
        <ul className="sheet-options">
          {orders.map((order) => (
            <li key={order.id}>
              <button
                type="button"
                className="sheet-option"
                onClick={() => {
                  setPending({ target: { kind: 'order', orderId: order.id, label: order.label }, values: null });
                  setPickingOrder(false);
                }}
              >
                {order.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (pickingBooking) {
    // A FAILED fetch leaves `bookings` null forever, so the loading line below would be
    // the last thing ever rendered and the error would never be seen. Caught by its own
    // test, which is the only reason it is not still here.
    if (bookings === null && problem !== null) {
      return (
        <div className="sheet" role="dialog" aria-label="Booking">
          <header className="sheet-header">
            <strong>Add to which booking?</strong>
            <button type="button" onClick={() => setPickingBooking(false)}>
              Cancel
            </button>
          </header>
          <p role="alert">{problem}</p>
        </div>
      );
    }
    if (bookings === null) return <p role="status">Loading bookings…</p>;
    // Only DRAFTS take new garments since 0.1.523 (maintainer, 1 Oct 2026: "nothing new should be
    // able to be added, only in drafts") — the server refuses a confirmed one by name.
    const open = bookings.filter((b) => b.status === 'draft');
    return (
      <div className="sheet" role="dialog" aria-label="Booking">
        <header className="sheet-header">
          <strong>Add to which booking?</strong>
          <button type="button" onClick={() => leavePicker(setPickingBooking)}>
            Cancel
          </button>
        </header>
        {open.length === 0 && <p>No open bookings — create one on the Bookings screen first.</p>}
        <ul className="sheet-options">
          {open.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                className="sheet-option"
                onClick={() => {
                  setPending({
                    target: {
                      kind: 'bookingAdd',
                      bookingId: b.id,
                      label: `“${b.title}” (${rangeLabel(b)}, ${b.status})`,
                      status: b.status,
                    },
                    values: null,
                  });
                  setPickingBooking(false);
                }}
              >
                {/* rangeLabel, not the raw days: an undated reservation (091) reads "No dates yet". */}
                “{b.title}” — {rangeLabel(b)} · {b.status}
                {b.clientName !== null ? ` · ${b.clientName}` : ''}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Value-picking stage rides OptionSheet — the same component intake trusts, so
  // search, recents and free entry behave identically here.
  if (picking !== null) {
    const field = picking.kind === 'field' ? meta.fields.find((f) => f.key === picking.key) : undefined;
    return (
      <>
      {confirmEl}
      <OptionSheet
        /**
         * FULL-BLEED HERE, AND ONLY HERE IN THIS FLOW (maintainer, 1 Aug 2026: "it should
         * open a full screen view to keep consistent with the edit items screen, which is
         * also a full screen view").
         *
         * Every other screen in the app opens this picker as a small anchored dropdown and
         * still should — see the note on the `fullscreen` prop. What makes this flow
         * different is that the screen underneath is `.sheet`, which is itself
         * `position: fixed; inset: 0`. A centred card on top of a full-screen sheet reads
         * as two competing layers rather than one step of a flow.
         */
        fullscreen
        title={targetLabel(picking)}
        options={
          picking.kind === 'brand'
            ? meta.brands
            : picking.kind === 'category'
              ? meta.categories.map((c) => c.path)
              : picking.kind === 'location'
                ? meta.locations.map((l) => l.name)
                : (field?.options ?? [])
        }
        selected={[]}
        /**
         * THE SAME ＋ ✎ ✕ AS EVERY OTHER PICKER (maintainer, 31 Jul 2026). A missing option
         * is noticed exactly when you are trying to pick it, and this sheet is often where
         * that happens — it is the screen used to fix a whole shelf at once.
         *
         * No `selected`/`onSelected` wiring: this sheet holds no prior value (it opens with
         * `selected={[]}`), so there is nothing for a rename to invalidate. Brand has no
         * tools at all — see the note on `allowFree` below.
         */
        manage={
          picking.kind === 'brand'
            ? brandManage({
                setMeta,
                usage: structureUsage,
                reloadUsage: () => void reloadUsage(),
                onOutcome: setBulkOutcome,
                canCreate: metaCan(meta, 'brands'),
                labels: meta.labels,
                canRenameHeading: metaCan(meta, 'builtinLabels'),
                confirm: confirmMerge,
              })
            : picking.kind === 'category'
            ? categoryManage(meta, {
                setMeta,
                usage: structureUsage,
                reloadUsage: () => void reloadUsage(),
                labels: meta.labels,
                canRenameHeading: metaCan(meta, 'builtinLabels'),
              })
            : picking.kind === 'location'
              ? locationManage(meta, {
                  setMeta,
                  usage: structureUsage,
                  reloadUsage: () => void reloadUsage(),
                  labels: meta.labels,
                  canRenameHeading: metaCan(meta, 'builtinLabels'),
                })
              : picking.kind === 'field'
                ? fieldOptionManage<IntakeMeta>(picking.key, setMeta)
                : undefined
        }
        multi={picking.kind === 'field' && field?.kind === 'multi_select'}
        // Brand and field values are genuinely open sets; category and location must
        // reference real rows, so no free entry there.
        allowFree={picking.kind === 'brand' || picking.kind === 'field'}
        onChange={(values) => {
          setPending({ target: picking, values });
          setPicking(null);
        }}
        onClose={() => setPicking(null)}
      />
      </>
    );
  }

  if (pending !== null) {
    return (
      <div className="sheet" role="dialog" aria-label="Confirm bulk edit">
        <p>{sentence(pending)}?</p>
        {problem !== null && <p role="alert">{problem}</p>}
        <p>
          <button type="button" disabled={applying} onClick={() => void apply(pending)}>
            {applying ? 'Applying…' : 'Apply'}
          </button>{' '}
          <button type="button" disabled={applying} onClick={() => setPending(null)}>
            Back
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="sheet" role="dialog" aria-label="Bulk edit">
      <header className="sheet-header">
        <strong>
          Edit {n} item{n === 1 ? '' : 's'}
        </strong>
        <button type="button" onClick={onClose}>
          Cancel
        </button>
      </header>
      {problem !== null && <p role="alert">{problem}</p>}
      {bulkOutcome !== null && <p role="status">{bulkOutcome}</p>}
      {/**
        * ONE ROW PER FIELD — reported 30 Jul: the list alternated a plain "Set X…" line
        * with a bordered "Clear X" button, so nothing read as a pair and the eye had to
        * re-derive the grouping on every row.
        *
        * Now each field is a row: its NAME, then Set and Clear beside it. The name is the
        * label rather than part of a sentence, which is what lets the two verbs be short.
        *
        * ADD TO A BOOKING, SAVE AS AN OUTFIT, ASSIGN AN ORDER and RETIRE are gone from
        * here. They are acts on a SELECTION, not edits to a field, and they now sit on the
        * selection bar where the selection is. This sheet does one thing again: change a
        * field on several garments at once. `openAt` still brings the promoted acts back
        * into their own pickers, so the flows are unchanged — only the door moved.
        */}
      <ul className="bulk-fields">
        {/**
         * ⚠️ THE ORDER DOOR IS BACK IN THE SHEET (0.1.168), and it is the ONLY one now.
         *
         * The comment above says these acts "now sit on the selection bar" — that stopped
         * being true in 0.1.167, and the column shortcut that replaced it was removed on
         * instruction. `setPickingOrder` was reachable ONLY from `openAt === 'order'`, so
         * without this button both assigning a selection to a source order AND
         * "＋ New box from N selected…" (which lives inside that picker) would have had no
         * entry point at all — a capability deleted by the removal of a shortcut to it.
         */}
        <li className="bulk-field">
          <span className="bulk-field-name">Source order</span>
          <span className="bulk-field-acts">
            <button type="button" className="chip" onClick={() => setPickingOrder(true)}>
              Assign…
            </button>
          </span>
        </li>
        {/* "Restore retired" moved to the item list's acts column (0.1.171), beside the
            Retire it undoes — it was one screen deeper than its own reversal. */}
        {(
          [
            { target: { kind: 'brand' } },
            { target: { kind: 'category' } },
            { target: { kind: 'location' } },
            ...meta.fields.map((f) => ({ target: { kind: 'field', key: f.key } })),
          ] as { target: Target }[]
        ).map(({ target }) => (
          <li className="bulk-field" key={targetLabel(target)}>
            <span className="bulk-field-name">{targetLabel(target)}</span>
            <span className="bulk-field-acts">
              <button type="button" className="chip chip-primary" onClick={() => setPicking(target)}>
                Set…
              </button>
              <button
                type="button"
                className="chip"
                aria-label={`Clear ${targetLabel(target)}`}
                onClick={() => setPending({ target, values: null })}
              >
                Clear
              </button>
            </span>
          </li>
        ))}
      </ul>
      {/**
        * ADD A FIELD, FROM HERE TOO (maintainer, 1 Aug 2026: "in the edit x amount of item
        * screen, I should have an option at the bottom to add a field").
        *
        * The same reasoning that put field management inside the dropdowns on 30 Jul: the
        * moment you notice a field is missing is the moment you are looking for it, and
        * this sheet is where somebody sets a value across a whole shelf. Previously they
        * had to cancel out, open one garment, edit it, add the field there, come back and
        * re-make the selection — which is long enough that the selection is usually lost.
        *
        * At the BOTTOM, matching the item edit screen, which moved its own button down on
        * the same day. Two screens listing the same fields must not disagree about where
        * the control that adds one lives.
        *
        * `setMeta` is all the wiring it needs: the field rows above are rendered straight
        * from `meta.fields`, so a new field appears in the list without a re-fetch.
        */}
      <AddFieldButton
        onMeta={(next) => {
          setMeta(next);
        }}
      />
    </div>
  );
}
